import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions";

import Hero from "../sections/Hero";
import WhyUs from "../sections/WhyUs";
import Products from "../sections/Products";
import ToolsSection from "../sections/ToolsSection";
import Testimonials from "../sections/Testimonials";
import Faq from "../sections/Faq";
import Contact from "../sections/Contact";
import CtaBand from "../sections/CtaBand";

import useSeo, {
  breadcrumbJsonLd,
  faqJsonLd,
  organizationJsonLd,
} from "../lib/seo";
import { FAQS } from "../data/trust";
import { SECTION_IDS } from "../data/site";
import { useL, useLang } from "../lib/useLang";
import { normalizePath, scrollToSection } from "../lib/navigation";

/**
 * Rutas del sitio anterior. Se mantienen vivas para no perder el
 * posicionamiento ni los enlaces compartidos: cada una lleva a su
 * sección equivalente dentro de la página única.
 */
const LEGACY_PATHS = {
  "/about": "por-que-elegirnos",
  "/products": "productos",
  "/products/cancer": "productos",
  "/products/8seguros": "productos",
  "/products/gastosfinales": "productos",
  "/products/retirement": "productos",
  "/testimonials": "testimonios",
  "/contact": "contacto",
};

export default function Landing() {
  const { t } = useTranslation();
  const L = useL();
  const lang = useLang();
  const [productId, setProductId] = useState(null);

  const jsonLd = useMemo(
    () => [
      organizationJsonLd(),
      faqJsonLd(FAQS.map((faq) => ({ q: L(faq.q), a: L(faq.a) }))),
      breadcrumbJsonLd(
        SECTION_IDS.map((id) => ({ name: id, path: `/#${id}` }))
      ),
    ],
    [L]
  );

  useSeo({
    description: t("hero.lead"),
    path: "/",
    lang,
    jsonLd,
  });

  // Llegadas por hash o por una URL antigua del sitio anterior.
  useEffect(() => {
    const legacy = LEGACY_PATHS[normalizePath()];
    const target = legacy ?? window.location.hash.replace("#", "");

    if (!target) return undefined;

    if (target === "unete") {
      window.location.replace("/unete");
      return undefined;
    }

    if (legacy && window.history?.replaceState) {
      window.history.replaceState(null, "", `/#${legacy}`);
    }

    const timer = window.setTimeout(() => scrollToSection(target), 260);
    return () => window.clearTimeout(timer);
  }, []);

  const openProduct = useCallback((id) => setProductId(id), []);
  const closeProduct = useCallback(() => setProductId(null), []);

  return (
    <div className="fsg-app">
      <Header />

      <main className="fsg-main" id="contenido">
        <Hero />
        <WhyUs />
        <Products
          openId={productId}
          onOpen={openProduct}
          onClose={closeProduct}
        />
        <ToolsSection onOpenProduct={openProduct} />
        <CtaBand />
        <Testimonials />
        <Faq />
        <Contact />
      </main>

      <Footer />
      <FloatingActions />
    </div>
  );
}
